/**
 * Utility functions for Phoenix 2
 */

import { call, take, fork } from 'redux-saga/effects';
import { wait, wCall } from 'phoenix2/state/wait/sagas';
import { CANCEL } from 'redux-saga';
import { fromJS, is } from 'immutable';

/**
 * Sleep for a given time, returning a promise
 * @param seconds the number of seconds to sleep for
 * @returns {Promise<T>|Promise} Promise which resolves when the sleeping
 * has finished
 */
export function asyncSleep(seconds, val = true) {
    return asyncSleepMs(seconds * 1000, val);
}

export function asyncSleepMs(milliseconds, val = true) {
    let timeoutId;
    const promise = new Promise(resolve => {
        timeoutId = setTimeout(() => resolve(val), milliseconds);
    });
    promise[CANCEL] = () => clearTimeout(timeoutId);

    return promise;
}

export function callWith(params, ...args) {
    let effect = call(...args);
    if (!params.inBackground) {
        effect = wait(effect);
    }
    return effect;
}

export function exitFullScreen() {
    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
    } else if (document.msExitFullScreen) {
        document.msExitFullScreen();
    } else {
        const videos = document.getElementsByTagName('video');
        if (videos.length <= 0 || !videos[0].webkitExitFullScreen) {
            return;
        }
        for (const video of videos) {
            video.webkitExitFullScreen();
        }
    }
}

/**
 * Add a set of listeners for fullscreen events that work in all browsers.
 * Returns an object containing the listeners which can be used to remove
 * them again, using removeFullscreenListeners
 * @param onFullScreenChange function to call when a fullscreenchange event occurs
 */
export const addFullScreenListeners = onFullScreenChange => {
    const fsListeners = {};
    [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'msfullscreenchange',
    ].forEach(eventType => {
        fsListeners[eventType] = document.addEventListener(eventType, onFullScreenChange, false);
    });
    return fsListeners;
};

/**
 * Remove a set of listeners that were added with the addFullScreenListeners function
 * @param listeners the object returned from addFullscreenListeners
 */
export const removeFullscreenListeners = listeners => {
    for (const eventType in listeners) {
        // skip loop if the property is from prototype
        if (!Object.prototype.hasOwnProperty.call(listeners, eventType)) {
            continue;
        }
        document.removeEventListener(eventType, listeners[eventType]);
    }
};

/**
 * A Saga effect for processing actions which takes every action and
 * processes it, like the redux-sagas takeEvery effect. However, it will
 * ignore any actions that arrive whilst processing the action and for a
 * given time interval afterwards.
 * Note, this different from Redux-Saga's throttle function in that an action
 * arriving during the throttling period is ignored completely rather that
 * being queued up to run at the end of the action.
 * This function is useful to simplify sagas for user actions (like button
 * clicks) where we only need one click of the button and so want to guard
 * against processing a double-click twice.
 * @param patternOrChannel an action, array of actions or filter function,
 * the same as the first parameter to a Redux-Saga take call
 * @param worker the function to be called when an action arrives (which is
 * passed the action as the first parameter
 * @param waitInterval a period measured in seconds that the function will wait
 * before accepting further actions. If this is set to 0 it will only
 * wait whilst the worker has not yet completed its task
 * @param args zero or more parameters for the function to be called. It will be
 * called with these parameters, with the action as the final parameter
 */
export function* throttledTakeEvery(patternOrChannel, worker, waitInterval, ...args) {
    while (true) {
        const action = yield take(patternOrChannel);
        yield call(worker, ...args, action);
        yield asyncSleep(waitInterval);
    }
}

/**
 * A saga effect for processing actions which takes every action which matches
 * an identifier. This means you can match actions not just on their type, but
 * also on any other action fields. The identifierFn is provided which shows how
 * to create an id from the action. 'takeLatest' means that if there is already
 * a task running with the same identifier, the existing one will be cancelled
 * and the latest task with that identifier will be forked.
 * @param {string|function} pattern - How to match the actions. See redux-saga
 * take(pattern) for docs.
 * @param {function} identifierFn - A function which is called with the incoming
 * action to product an identifier string.
 * @param {function} fn - the function to send the matched action to
 * @param  {...any} args - zero or more args to send to fn
 */
export const takeLatestByIdentifier = (pattern, identifierFn, fn, ...args) =>
    fork(function*() {
        const tasks = {};
        while (true) {
            const action = yield take(pattern);
            const id = identifierFn(action);
            if (tasks[id] && tasks[id].isRunning()) {
                tasks[id].cancel();
            }
            tasks[id] = yield fork(fn, ...args.concat(action));
        }
    });

/**
 * A saga effect for processing actions which takes every action which matches
 * an identifier. This means you can match actions not just on their type, but
 * also on any other action fields. The identifierFn is provided which shows how
 * to create an id from the action. 'takeOnce' means that only one action of any
 * id will ever be called. Subsequent actions with the same id will be ignored.
 * @param {string|function} pattern - How to match the actions. See redux-saga
 * take(pattern) for docs.
 * @param {function} identifierFn - A function which is called with the incoming
 * action to product an identifier string.
 * @param {function} fn - the function to send the matched action to
 * @param  {...any} args - zero or more args to send to fn
 */
export const takeOnceByIdentifier = (pattern, identifierFn, fn, ...args) =>
    fork(function*() {
        const ids = new Set();
        while (true) {
            const action = yield take(pattern);
            const id = identifierFn(action);
            if (!ids.has(id)) {
                ids.add(id);
                yield fork(fn, ...args.concat(action));
            }
        }
    });

/**
 * A saga effect for processing actions which takes every action which matches
 * an identifier. This means you can match actions not just on their type, but
 * also on any other action fields. The identifierFn is provided which shows how
 * to create an id from the action. 'takeLeading' means that if there is already
 * a task with the same id running, the new action will be ignored. If there had
 * previously been a task with the same id but it has completed, the new action
 * will be taken.
 * @param {string|function} pattern - How to match the actions. See redux-saga
 * take(pattern) for docs.
 * @param {function} identifierFn - A function which is called with the incoming
 * action to product an identifier string.
 * @param {function} fn - the function to send the matched action to
 * @param  {...any} args - zero or more args to send to fn
 */
export const takeLeadingByIdentifier = (pattern, identifierFn, fn, ...args) =>
    fork(function*() {
        const tasks = {};
        while (true) {
            const action = yield take(pattern);
            const id = identifierFn(action);
            if (tasks[id] && !tasks[id].isRunning()) {
                delete tasks[id];
            }
            if (!tasks[id]) {
                tasks[id] = yield fork(fn, ...args.concat(action));
            }
        }
    });

/**
 * Determines whether a string contains a number
 */
export const isNumber = str => !isNaN(+str);

/**
 * Determines whether a string is only letters
 * @param string
 * @returns {boolean}
 */
export const isOnlyLetters = string => /^[a-zA-Z]+$/.test(string);

/**
 * Removes whitespace from a string
 * @param string
 * @returns {*} a new string with the whitespace removed
 */
export const removeWhitespace = string => string.replace(/\s+/g, '');

/**
 * Determines whether two JavaScript objects are equivalent - i.e. they have
 * exactly the same properties and property values. (We cheat and convert to
 * Immutable objects, then compare them).
 * @param object1
 * @param object2
 */
export const areEquivalent = (object1, object2) => is(fromJS(object1), fromJS(object2));

/**
 * Turns an object of key-value pairs for URL query parameters into a query
 * parameters string - i.e. encoded values separated by &s
 * @param data the key-value pairs
 * @returns {string} the query parameters string
 */
export const encodeQueryParameters = data => {
    const ret = [];
    for (const d in data) {
        if (Object.prototype.hasOwnProperty.call(data, d)) {
            ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
        }
    }
    return ret.join('&');
};

export { wait, wCall };

/**
 * Check the browser supports local storage
 * @returns {Boolean}
 */
export const localStorageExists = () => {
    const test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Debounce a function
 * @param callback Function to debounce
 * @param wait time in millis to wait before calling function, if another call
 * happens during this interval, then reset the time
 * @returns {Function}
 */
export function debounce(callback, wait) {
    let timeout = null;
    return function() {
        const next = () => callback.apply(this, arguments);
        clearTimeout(timeout);
        timeout = setTimeout(next, wait);
    };
}
